<template>
  <div>
    <loading-screen :is-loading="isSaving"></loading-screen>
    <errors :error-list="serverErrors"></errors>

    <suggest-contacts-modal
      :hide-values="suggestContactsValuesToHide"
      property="phone_number"
      :modal-id="suggestModalId"
      @contact-selected="handleSuggestContactSelected"
      :whatsapp="true"
    ></suggest-contacts-modal>

    <!-- Description -->
    <div class="card shadow-none card-action">
      <div class="card-header">
        <div class="avatar-xxs d-inline-block mr-3">
          <span
            class="avatar-title rounded bg-soft-primary text-primary font-size-14"
          >
            {{ $auth.user.name.charAt(0) }}
          </span>
        </div>
        <b class="d-inline-block">Send WhatsApp</b>
      </div>
      <!-- To field -->
      <div class="card-menubar card-menubar-top py-1 d-flex align-items-center">
        <label class="p-0 m-0" for="toField"><b>To:</b></label>
        <b-form-tags
          remove-on-delete
          tag-pills
          input-id="toField"
          v-model="toAddresses"
          class="border-0"
          placeholder="Add mobile number..."
          add-on-change
          duplicate-tag-text="Duplicate mobile number(s)"
        ></b-form-tags>
        <div class="flex-shrink-1">
          <a href="#" @click="suggestForField('to')" class="mr-3">Suggest</a>
        </div>
      </div>
      <!-- / To field -->
      <!-- From field -->
      <div class="card-menubar card-menubar-top py-1 d-flex align-items-center">
        <label class="p-0 m-0" for="fromField"><b>From:</b></label>
        <b-form-select
          v-model="from"
          :options="fromOptions"
          class="border-0 my-1"
          placeholder="Select phone number to send from"
        ></b-form-select>
      </div>
      <!-- / From field -->
      <div class="card-body p-2">
        <textarea
          name="body"
          v-model="body"
          class="form-control border-0"
          rows="5"
          :disabled="!!twilioTemplateSid"
        ></textarea>
        <div v-if="hasTemplateVariables" class="mt-3">
          <div class="bg-light">
            <div class="card-body p-2">
              <h6 class="mb-3">Template Variables</h6>
              <table class="table table-sm template-variables-table mb-0">
                <tbody>
                  <tr v-for="(value, key) in templateVariables" :key="key">
                    <td class="variable-label">Variable {{key}}</td>
                    <td>
                      <input 
                        type="text" 
                        class="form-control"
                        :class="{'is-invalid': showValidationErrors && templateVariableErrors[key]}" 
                        v-model="templateVariables[key]"
                        :placeholder="'Enter value for variable ' + key"
                      >
                      <div v-if="showValidationErrors && templateVariableErrors[key]" class="invalid-feedback">
                        {{ templateVariableErrors[key] }}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div class="card-menubar border-top">
        <div class="menubar">
          <a
            href="#"
            class="btn btn-itg-white btn-sm menubar__button"
            @click.prevent="openApplyWhatsappTemplateModal"
          >
            <i class="fas fa-paste"></i> Template
          </a>
          <a
            href="#"
            class="btn btn-itg-white btn-sm menubar__button"
            @click.prevent="openMediaUploader"
          >
            <i class="fas fa-images"></i> Image
            <span v-if="imageUrl" class="badge badge-light ml-1">1</span>
          </a>
          <a
            href="#"
            class="btn btn-itg-white btn-sm menubar__button"
            @click.prevent="openFileUploader"
          >
            <i class="fas fa-file"></i> File
            <span v-if="fileUrl" class="badge badge-light ml-1">1</span>
          </a>
        </div>
      </div>
      <div class="card-footer text-right">
        <button type="button" class="btn btn-itg mr-2" @click="attemptCancel">
          Cancel
        </button>

        <button type="button" class="btn btn-itg-dark" @click="send">
          Send
        </button>
      </div>
    </div>
    <!-- / Description -->

    <apply-ticket-whatsapp-template-modal
      :modal-id="whatsappTemplateModalId"
      @template-selected="handleTemplateSelected"
    ></apply-ticket-whatsapp-template-modal>

    <upload-whatsapp-media-modal
      :modal-id="whatsappMediaModalId"
      :ticket-id="ticket.id"
      :existing-image-url="imageUrl"
      @file-uploaded="handleMediaUploaded"
      @remove-image="handleRemoveImage"
    ></upload-whatsapp-media-modal>

    <upload-whatsapp-file-modal
      :modal-id="whatsappFileModalId"
      :ticket-id="ticket.id"
      :existing-file-url="fileUrl"
      @file-uploaded="handleFileUploaded"
      @remove-file="handleRemoveFile"
    ></upload-whatsapp-file-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import Forms from "../../../../../mixins/Forms";
import axios from "axios";
import SuggestContactsModal from "./Reply/SuggestContactsModal";
import ApplyTicketWhatsappTemplateModal from "./SendWhatsapp/ApplyTicketWhatsappTemplateModal.vue";
import UploadWhatsappMediaModal from "./SendWhatsapp/UploadWhatsappMediaModal.vue";
import UploadWhatsappFileModal from "./SendWhatsapp/UploadWhatsappFileModal.vue";

export default {
  name: "SendSms",
  data() {
    return {
      suggestModalId: "reply-whatsapp-suggest-modal",
      fieldSuggestingFor: null,

      isSaving: false,
      serverErrors: [],
      toAddresses: [],
      body: "",
      from: null,

      twilioPhoneNumbers: [],
      isLoadingTwilioPhonNumbers: false,
      whatsappTemplateModalId: "apply-whatsapp-template-modal",

      whatsappMediaModalId: "upload-whatsapp-media-modal",
      whatsappFileModalId: "upload-whatsapp-file-modal",

      imageUrl: null,
      fileUrl: null,

      twilioTemplateSid: null,
      templateVariables: {},
      templateVariableErrors: {},
      showValidationErrors: false,
    };
  },
  computed: {
    ...mapGetters("ticketViewer", {
      ticket: "ticket",
    }),

    hasTemplateVariables() {
      return Object.keys(this.templateVariables).length > 0;
    },

    hasTemplateVariableErrors() {
      return Object.values(this.templateVariableErrors).some(error => error);
    },

    isLoading() {
      return this.isLoadingTwilioPhoneNumbers;
    },

    fromOptions() {
      let filteredTwilioPhoneNumbers = _.filter(
        this.twilioPhoneNumbers,
        function (phoneNumber) {
          return phoneNumber.whatsapp_enabled;
        }
      );

      return _.map(filteredTwilioPhoneNumbers, function (phoneNumber) {
        return {
          value: phoneNumber.phone_number,
          text: phoneNumber.name + " (" + phoneNumber.phone_number + ")",
          phone_number: phoneNumber.phone_number
        };
      });
    },

    suggestContactsValuesToHide() {
      if (this.fieldSuggestingFor === "to") {
        return this.toAddresses;
      }

      return [];
    },

    whatsappContacts() {
      if (!this.ticket || !this.ticket.contacts) {
        return [];
      }
      return this.ticket.contacts.filter(contact => contact.communicates_via_whatsapp);
    }
  },
  components: { SuggestContactsModal, ApplyTicketWhatsappTemplateModal, UploadWhatsappMediaModal, UploadWhatsappFileModal },
  mixins: [Forms],
  mounted() {
    this.loadTwilioPhoneNumbers();

    // Auto-fill if there's only one WhatsApp contact
    if (this.whatsappContacts.length === 1) {
      this.toAddresses = [this.whatsappContacts[0].phone_number];
    }

    this.$nextTick(function () {
      window.setTimeout(function () {
        let container = document.getElementById(
          "ticket-main-section-left-column"
        );
        container.scrollTo({
          top: container.scrollHeight,
          behavior: "smooth",
          cancelable: false,
        });
      }, 1);
    });
  },
  methods: {
    ...mapActions("ticketViewer", {
      cancelAction: "cancelAction",
      actionComplete: "actionComplete",
    }),

    loadTwilioPhoneNumbers() {
      let vm = this;
      vm.isLoadingTwilioPhoneNumbers = true;

      axios
        .get(API_BASE + "/twilio-phone-numbers")
        .then((r) => {
          vm.isLoadingTwilioPhoneNumbers = false;
          vm.twilioPhoneNumbers = r.data;

          let firstPhoneNumber = _.first(vm.fromOptions);
          if (firstPhoneNumber) {
            vm.from = firstPhoneNumber.phone_number;
          }
        })
        .catch((e) => {
          vm.isLoadingTwilioPhoneNumbers = false;
          vm.setErrors(e);
        });
    },

    send() {
      if (this.isLoading) {
        return false;
      }

      // Validate template variables if using a Twilio template
      if (this.twilioTemplateSid) {
        this.showValidationErrors = true;
        if (!this.validateTemplateVariables()) {
          return false;
        }
      }

      // If image and media are both set, return false
      if (this.imageUrl && this.fileUrl) {
        this.$notify.error({
          title: "Error!",
          message: "You can only send one image or file at a time"
        });
        return false;
      }

      let vm = this;
      vm.isSaving = true;
      vm.serverErrors = [];

      const payload = {
        to: this.toAddresses,
        from: this.from,
        body: vm.body,
      };

      if (this.imageUrl) {
        payload.media_url = this.imageUrl;
      }

      if (this.fileUrl) {
        payload.media_url = this.fileUrl;
      }

      if (this.twilioTemplateSid) {
        payload.twilio_template_sid = this.twilioTemplateSid;
        payload.template_variables = this.templateVariables;
      }

      axios
        .post(API_BASE + "/tickets/" + this.ticket.id + "/whatsapp", payload)
        .then((r) => {
          vm.isSaving = false;
          vm.twilioTemplateSid = null;
          vm.templateVariables = {};
          vm.templateVariableErrors = {};
          vm.showValidationErrors = false;
          this.$notify.success({
            title: "Success!",
            message: "WhatsApp sent successfully",
          });
          this.actionComplete();
        })
        .catch((e) => {
          vm.isSaving = false;
          vm.setErrors(e);
        });
    },

    attemptCancel() {
      let vm = this;
      swal({
        title: "Are you sure you want to cancel?",
        text: "You have unsaved changes and your data will be lost.",
        icon: "warning",
        buttons: {
          cancel: true,
          confirm: "Leave without saving",
        },
        dangerMode: true,
      }).then((shouldCancel) => {
        if (shouldCancel) {
          vm.twilioTemplateSid = null;
          vm.templateVariables = {};
          vm.templateVariableErrors = {};
          vm.showValidationErrors = false;
          vm.cancelAction();
        }
      });
    },

    suggestForField(field) {
      this.fieldSuggestingFor = field;
      this.$bvModal.show(this.suggestModalId);
    },

    handleSuggestContactSelected(contact) {
      if (this.fieldSuggestingFor === "to") {
        this.toAddresses.push(contact.phone_number);
      }
    },

    openApplyWhatsappTemplateModal() {
      this.$bvModal.show(this.whatsappTemplateModalId);
    },

    handleTemplateSelected(template) {
      this.body = template.body;
      this.twilioTemplateSid = template.twilio_template_sid || null;
      this.showValidationErrors = false;
      
      // Parse template variables
      if (template.twilio_template_sid) {
        const variables = {};
        const matches = template.body.match(/\{\{(\d+)\}\}/g) || [];
        matches.forEach(match => {
          const varName = match.replace(/[{}]/g, '');
          variables[varName] = '';
        });
        this.templateVariables = variables;
        this.templateVariableErrors = {};
      } else {
        this.templateVariables = {};
        this.templateVariableErrors = {};
      }
    },

    validateTemplateVariables() {
      const errors = {};
      let isValid = true;

      Object.entries(this.templateVariables).forEach(([key, value]) => {
        if (!value || value.trim() === '') {
          errors[key] = 'This variable is required';
          isValid = false;
        } else {
          errors[key] = '';
        }
      });

      this.templateVariableErrors = errors;
      return isValid;
    },

    openMediaUploader() {
      this.$bvModal.show(this.whatsappMediaModalId);
    },

    openFileUploader() {
      this.$bvModal.show(this.whatsappFileModalId);
    },

    handleMediaUploaded(response) {
      this.imageUrl = response.url;
      this.$bvModal.hide(this.whatsappMediaModalId);
      this.$notify.success({
        title: "Success!",
        message: "Image uploaded successfully"
      });
    },

    handleFileUploaded(response) {
      this.fileUrl = response.url;
      this.$bvModal.hide(this.whatsappFileModalId);
      this.$notify.success({
        title: "Success!",
        message: "File uploaded successfully"
      });
    },

    handleRemoveImage() {
      this.imageUrl = null;
      this.$notify.success({
        title: "Success!",
        message: "Image removed successfully"
      });
    },

    handleRemoveFile() {
      this.fileUrl = null;
      this.$notify.success({
        title: "Success!",
        message: "File removed successfully"
      });
    }
  },
};
</script>

<style scoped>
.card {
  background-color: #f5f7f9;
}

textarea:disabled {
  background-color: #f8f9fa;
  cursor: not-allowed;
}

.template-variables-table {
  margin-top: -0.5rem;
}

.template-variables-table td {
  border: none;
  padding: 0.4rem;
  vertical-align: middle;
}

.variable-label {
  width: 100px;
  color: #6c757d;
  font-weight: 500;
  font-size: 0.875rem;
}

.template-variables-table .form-control {
  background-color: white;
  border: 1px solid #dee2e6;
}

.template-variables-table .form-control:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0,123,255,.25);
}

.template-variables-table .form-control.is-invalid {
  border-color: #dc3545;
  padding-right: calc(1.5em + 0.75rem);
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='%23dc3545' viewBox='0 0 12 12'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23dc3545' stroke='none'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right calc(0.375em + 0.1875rem) center;
  background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
}

.template-variables-table .form-control.is-invalid:focus {
  border-color: #dc3545;
  box-shadow: 0 0 0 0.2rem rgba(220,53,69,.25);
}

.template-variables-table .invalid-feedback {
  display: block;
  font-size: 0.75rem;
  color: #dc3545;
  margin-top: 0.25rem;
}
</style>
